import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  MutableRefObject,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';

import { useDebounce } from 'hooks/useDebounce';
import { ValueLabel, ValueLabelGroup } from 'types/types';

import Input from '../../../../Common/InputComponents/Input';

interface Props {
  items: ValueLabelGroup[];
  handleChangeItems: (
    itemsUpdated: ValueLabelGroup[],
    searchFunctionality?: boolean
  ) => void;
  allItems: MutableRefObject<ValueLabelGroup[]>;
}

export interface Ref {
  searchValue: string;
  searchInValueLabelGroupsAll(
    groups: ValueLabelGroup[],
    searchValue: string
  ): ValueLabelGroup[];
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
}

const ListSearchBar: ForwardRefRenderFunction<Ref, Props> = (
  { items, handleChangeItems, allItems },
  ref
) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  const debouncedValue = useDebounce(searchValue, 300);

  const handleChange = (value: string | undefined) => {
    setSearchValue(value ?? '');
  };

  const searchInValueLabelGroupsAll = (
    groups: ValueLabelGroup[],
    searchValue: string
  ): ValueLabelGroup[] => {
    const numGroups = groups.length;
    const groupsFound: ValueLabelGroup[] = [];
    // Using traditional for loops to maximize performance for large datasets
    for (let i = 0; i < numGroups; i++) {
      const matches: ValueLabel[] = [];
      const group = groups[i];
      const numItems = group.items.length;

      for (let j = 0; j < numItems; j++) {
        const item = group.items[j];

        // If the item's value includes the searchValue, add it to the matches array
        if (item.label.toLowerCase().includes(searchValue.toLowerCase())) {
          matches.push(item);
        }
      }
      if (matches.length > 0) {
        groupsFound.push({
          group: group.group,
          items: matches,
        });
      }
    }

    return groupsFound;
  };

  useEffect(() => {
    if (debouncedValue != '') {
      const foundElements = searchInValueLabelGroupsAll(items, debouncedValue);
      handleChangeItems(foundElements, true);
    } else {
      handleChangeItems(allItems.current, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  useImperativeHandle(ref, () => ({
    searchValue: debouncedValue,
    searchInValueLabelGroupsAll,
    setSearchValue,
  }));

  return (
    <div>
      <Input
        size="small"
        value={searchValue}
        className="input-elements-output"
        id="outlined-multiline-static"
        inputRef={searchRef}
        handleOnChange={handleChange}
        placeholder="Search"
      />
    </div>
  );
};

export default forwardRef<Ref, Props>(ListSearchBar);
